import { ReactNode, useMemo } from "react";
import { AttributeTypes } from "@iddataweb/idw-common-ui/enums";
import { PrefilledAttributeTypeFieldValidationErrors, PrefilledAttributeValidationErrors } from "@idw-types/common/prefilled-attribute-validation-errors";
import { FormattedRpServiceConfigurationAttributeWithFields } from "@idw-types/data/rp-service-configuration";
import { DATE_ATTRIBUTE_TYPE_FIELD_IDs, DOB_ATTRIBUTE_TYPE_FIELD_IDs, TELEPHONE_ATTRIBUTE_TYPE_FIELD_IDs } from "@constants/self-service-constants";
import { PrefilledAttributeDateComponent } from "@containers/common/self-service-prefilled-attributes/self-service-prefilled-attribute-axnui-component/prefilled-attribute-date-component/prefilled-attribute-date-component";
import { PrefilledAttributeTelephoneComponent } from "@containers/common/self-service-prefilled-attributes/self-service-prefilled-attribute-axnui-component/prefilled-attribute-telephone-component/prefilled-attribute-telephone-component";

type SelfServicePrefilledAttributeAxnuiComponentProps = {
    attributeTypesWithField: FormattedRpServiceConfigurationAttributeWithFields;
    attributeFieldValues: { [key: string]: string };
    attributeRequired: boolean;
    onAttributeFieldChange: (changeObject: { [key: string]: { [key: string]: string } }) => void;
    validationErrors: PrefilledAttributeValidationErrors;
}
export const SelfServicePrefilledAttributeAxnuiComponent = (props: SelfServicePrefilledAttributeAxnuiComponentProps) => {
    const {
        attributeTypesWithField,
        attributeFieldValues,
        attributeRequired,
        onAttributeFieldChange,
        validationErrors
    } = props;

    const component = useMemo(() => {
        switch (attributeTypesWithField.attributeType.name) {
            case AttributeTypes.DOB:
            case AttributeTypes.DATE: {
                return (
                    <PrefilledAttributeDateComponent
                        attributeRequired={attributeRequired}
                        attributeFieldValues={attributeFieldValues}
                        attributeTypesWithField={attributeTypesWithField}
                        onAttributeFieldChange={onAttributeFieldChange}
                    />
                );
            }
            case AttributeTypes.INTERNATIONAL_TELEPHONE: {
                return (
                    <PrefilledAttributeTelephoneComponent
                        attributeRequired={attributeRequired}
                        attributeFieldValues={attributeFieldValues}
                        attributeTypesWithField={attributeTypesWithField}
                        onAttributeFieldChange={onAttributeFieldChange}
                    />
                );
            }

            default: {
                return null;
            }
        }
    }, [attributeRequired, attributeTypesWithField, attributeFieldValues, onAttributeFieldChange]);

    const getValidationErrorForAttributeTypeFieldsWithPriority = (
        errors: { [key: string]: PrefilledAttributeTypeFieldValidationErrors },
        attributeTypeFieldIds: Array<string>
    ): ReactNode | null => {
        let finalValidationError = null;

        for (let i = 0; i < attributeTypeFieldIds.length; i++) {
            const fieldId = attributeTypeFieldIds[i];
            if (errors[fieldId]?.pattern) {
                finalValidationError = errors[fieldId].pattern;
                break;
            }
            if (errors[fieldId]?.required) {
                finalValidationError = errors[fieldId].required;
                break;
            }
            if (errors[fieldId]?.customValidator) {
                finalValidationError = errors[fieldId].customValidator;
                break;
            }
            if (errors[fieldId]?.phoneValidator) {
                finalValidationError = errors[fieldId].phoneValidator;
                break;
            }
        }

        return finalValidationError
    };

    const validationError = useMemo(() => {
        if (Object.keys(validationErrors[attributeTypesWithField.attributeType.id] || {}).length > 0) {
            switch (attributeTypesWithField.attributeType.name) {
                case AttributeTypes.DATE:
                    return getValidationErrorForAttributeTypeFieldsWithPriority(
                        validationErrors[attributeTypesWithField.attributeType.id],
                        [DATE_ATTRIBUTE_TYPE_FIELD_IDs.month, DATE_ATTRIBUTE_TYPE_FIELD_IDs.day, DATE_ATTRIBUTE_TYPE_FIELD_IDs.year]
                    );
                case AttributeTypes.DOB:
                    return getValidationErrorForAttributeTypeFieldsWithPriority(
                        validationErrors[attributeTypesWithField.attributeType.id],
                        [DOB_ATTRIBUTE_TYPE_FIELD_IDs.month, DOB_ATTRIBUTE_TYPE_FIELD_IDs.day, DOB_ATTRIBUTE_TYPE_FIELD_IDs.year]
                    );
                case AttributeTypes.INTERNATIONAL_TELEPHONE:
                    return getValidationErrorForAttributeTypeFieldsWithPriority(
                        validationErrors[attributeTypesWithField.attributeType.id],
                        [TELEPHONE_ATTRIBUTE_TYPE_FIELD_IDs.phone]
                    );
            }
        }

        return null;
    }, [validationErrors, attributeTypesWithField]);

    return (<div>
        {component}
        {validationError}
    </div>);
}